import { downloadFile, requestJson } from "@/lib/api";
import type { Receiving, ReceivingItem } from "@/lib/types";

interface ReceivingItemDto {
  productId: string;
  productName: string;
  unit: string;
  quantity: number;
}

interface ReceivingDto {
  id: string;
  supplierId: string;
  supplierName: string;
  receivedAt: string;
  invoiceNumber?: string | null;
  items: ReceivingItemDto[];
  registeredByEmail: string;
  registeredByName?: string | null;
  deliveryPersonName?: string | null;
  deliveryPersonDocument?: string | null;
  signatureUrl?: string | null;
  receiptPdfUrl?: string | null;
  createdAt: string;
  invoiceChangedAt?: string | null;
  invoiceChangedByEmail?: string | null;
  invoiceChangedByName?: string | null;
  invoiceChangeReason?: string | null;
}

function toReceivingItem(dto: ReceivingItemDto): ReceivingItem {
  return {
    productId: dto.productId,
    productName: dto.productName,
    unit: dto.unit,
    quantity: dto.quantity,
  };
}

function toReceiving(dto: ReceivingDto): Receiving {
  return {
    id: dto.id,
    supplierId: dto.supplierId,
    supplierName: dto.supplierName,
    receivedAt: dto.receivedAt,
    invoiceNumber: dto.invoiceNumber ?? null,
    items: dto.items.map(toReceivingItem),
    registeredByEmail: dto.registeredByEmail,
    registeredByName: dto.registeredByName ?? null,
    deliveryPersonName: dto.deliveryPersonName ?? null,
    deliveryPersonDocument: dto.deliveryPersonDocument ?? null,
    signatureUrl: dto.signatureUrl ?? null,
    receiptPdfUrl: dto.receiptPdfUrl ?? null,
    createdAt: dto.createdAt,
    invoiceChangedAt: dto.invoiceChangedAt ?? null,
    invoiceChangedByEmail: dto.invoiceChangedByEmail ?? null,
    invoiceChangedByName: dto.invoiceChangedByName ?? null,
    invoiceChangeReason: dto.invoiceChangeReason ?? null,
  };
}

export async function updateReceivingInvoice(
  id: string,
  input: { invoiceNumber: string; reason?: string },
): Promise<Receiving> {
  return toReceiving(await requestJson<ReceivingDto>(`/api/v1/receivings/${id}/invoice`, {
    method: "PATCH",
    body: input,
  }));
}

export async function updateReceiving(
  id: string,
  input: {
    supplierId: string;
    receivedAt: string;
    invoiceNumber?: string;
    items: Array<{
      productId: string;
      unit: string;
      quantity: number;
    }>;
  },
): Promise<Receiving> {
  return toReceiving(await requestJson<ReceivingDto>(`/api/v1/receivings/${id}`, {
    method: "PUT",
    body: input,
  }));
}

export async function deleteReceiving(id: string): Promise<void> {
  await requestJson<void>(`/api/v1/receivings/${id}`, { method: "DELETE" });
}

export interface ReceivingFilters {
  supplierId?: string;
  productId?: string;
  from?: string;
  to?: string;
}

function buildQuery(filters?: ReceivingFilters): string {
  const params = new URLSearchParams();
  if (filters?.supplierId) params.set("supplierId", filters.supplierId);
  if (filters?.productId) params.set("productId", filters.productId);
  if (filters?.from) params.set("from", filters.from);
  if (filters?.to) params.set("to", filters.to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchReceivings(filters?: ReceivingFilters): Promise<Receiving[]> {
  const list = await requestJson<ReceivingDto[]>(`/api/v1/receivings${buildQuery(filters)}`);
  return list.map(toReceiving);
}

export async function createReceiving(input: {
  supplierId: string;
  receivedAt: string;
  invoiceNumber?: string;
  deliveryPersonName?: string;
  deliveryPersonDocument?: string;
  signatureBase64?: string;
  items: Array<{
    productId: string;
    unit: string;
    quantity: number;
  }>;
}): Promise<Receiving> {
  return toReceiving(await requestJson<ReceivingDto>("/api/v1/receivings", {
    method: "POST",
    body: input,
  }));
}

export async function exportReceivings(format: "xlsx" | "pdf" | "csv", filters?: ReceivingFilters): Promise<void> {
  const path = `/api/v1/receivings/export.${format}${buildQuery(filters)}`;
  await downloadFile(path, `recebimentos.${format}`);
}
